import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";

// @ts-ignore - JS コンポーネント（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import HelpHint from "../../components/help-hint/HelpHint";
import SvgLoadingAnimation from "../../components/loading";
import InlineLoading from "../../components/loading/InlineLoading";
import Table, { TableColumn } from "../../components/table/Table";
import Button from "../../components/button/Button";
import IconButton from "../../components/button/IconButton";
import ContentTag from "../../components/content-tag/ContentTag";
import {
  useGetAdminGitHubOverviewQuery,
  useGetAdminGitHubRepositoriesQuery,
  type AdminGitHubRepository,
} from "../../app/services/github";
import GitHubRepositorySettingsModal from "./components/GitHubRepositorySettingsModal";
import GitHubWriteLogsModal from "./components/GitHubWriteLogsModal";
import { POSITION_LABELS } from "./positions";

const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap16",
})`
  padding: 24px 16px 80px;
`;

const ClickableRow = styled.tr`
  cursor: pointer;

  @media (hover: hover) {
    &:hover td {
      background: var(--soremo-background);
    }
  }
`;

const COLUMNS: TableColumn[] = [
  { key: "repository", header: "リポジトリ", minWidth: 200 },
  { key: "projects", header: "対象プロジェクト", minWidth: 180 },
  { key: "viewers", header: "閲覧", width: 160, minWidth: 120 },
  { key: "editors", header: "書き込み", width: 200, minWidth: 140 },
  { key: "period", header: "期間", width: 96, minWidth: 80 },
  { key: "logs", header: "", width: 48, minWidth: 48, resizable: false },
];

const editorsText = (repository: AdminGitHubRepository): string => {
  const names = repository.settings.roles.map((r) =>
    r.position ? POSITION_LABELS[r.position] ?? r.position : r.user?.display_name ?? "",
  );
  return names.length > 0 ? names.join("、") : "プロジェクト管理者のみ";
};

/**
 * GitHub リポジトリ管理ページ（/console/github、ADMIN = master_admin のみ）。
 *
 * 共有ライブラリと同じく、リポジトリを独立して登録し、プロジェクトに紐付けて
 * 閲覧ポジション・書き込みメンバーを設定する。GitHub へのアクセスは全体で 1 つの
 * GitHub App（soremon-bot）で行う。行クリックで設定、記録アイコンで書き込み記録。
 */
const AdminGitHubRepositories: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } = useGetCurrentUserQuery();
  const isAdmin = currentUser?.data?.role === "ADMIN";

  const { data: overviewData } = useGetAdminGitHubOverviewQuery(undefined, { skip: !isAdmin });
  const { data, isLoading, isError } = useGetAdminGitHubRepositoriesQuery(undefined, {
    skip: !isAdmin,
  });
  const overview = overviewData?.data;
  const repositories = data?.data?.repositories ?? [];

  const [editTarget, setEditTarget] = useState<AdminGitHubRepository | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [logsTarget, setLogsTarget] = useState<AdminGitHubRepository | null>(null);

  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }
  if (!isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  const notConfigured = overview !== undefined && !overview.configured;

  const renderRows = () => {
    const message = isLoading ? (
      <InlineLoading visible />
    ) : isError ? (
      "リポジトリの取得に失敗しました"
    ) : repositories.length === 0 ? (
      "登録済みのリポジトリはありません"
    ) : null;
    if (message) {
      return (
        <tr>
          <td colSpan={COLUMNS.length} className="bodytext-13 u-text-light-gray u-text-center">
            {message}
          </td>
        </tr>
      );
    }
    return repositories.map((repository) => {
      const { settings } = repository;
      return (
        <ClickableRow key={repository.id} onClick={() => setEditTarget(repository)}>
          <td className="bodytext-13">
            <a
              href={repository.html_url}
              target="_blank"
              rel="noreferrer"
              className="u-text-blue"
              onClick={(e) => e.stopPropagation()}
            >
              {repository.full_name}
            </a>
          </td>
          <td className="bodytext-13">
            <div className="u-row u-wrap u-gap4">
              {settings.projects.map((p) => (
                <ContentTag key={p.id}>{p.name}</ContentTag>
              ))}
            </div>
          </td>
          <td className="bodytext-13">
            {settings.allowed_positions.length > 0
              ? settings.allowed_positions.map((p) => POSITION_LABELS[p] ?? p).join("、")
              : "全メンバー"}
          </td>
          <td className="bodytext-13">{editorsText(repository)}</td>
          <td className="bodytext-13">{settings.allow_completed ? "完了後も可" : "進行中のみ"}</td>
          <td className="table-cell-visible u-text-center" onClick={(e) => e.stopPropagation()}>
            <IconButton
              icon="history"
              size={18}
              title="書き込み記録"
              ariaLabel={`${repository.full_name} の書き込み記録`}
              onClick={() => setLogsTarget(repository)}
            />
          </td>
        </ClickableRow>
      );
    });
  };

  return (
    <PageWrapper>
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="GITHUB" />
        <HelpHint
          description={
            "soremon から閲覧・更新できる GitHub リポジトリを管理します。共有ライブラリと同じく、対象プロジェクトのメンバーが閲覧でき、" +
            "書き込み（ブランチ作成・コミット・PR 作成）は指定したメンバーだけができます。コミットの作者は soremon-bot で、依頼者名がメッセージに入ります。"
          }
        />
      </div>

      {notConfigured && (
        <p className="bodytext-13 u-text-light-gray u-m0">
          GitHub App が設定されていません（環境変数 GITHUB_APP_ID / GITHUB_APP_PRIVATE_KEY）。
        </p>
      )}

      <div className="u-row u-gap8">
        <Button
          size="small"
          leftIcon="add"
          disabled={notConfigured}
          onClick={() => setIsCreating(true)}
        >
          リポジトリを登録
        </Button>
        {overview?.install_url && (
          <Button
            size="small"
            theme="secondary"
            variant="outlined"
            leftIcon="open_in_new"
            onClick={() => window.open(overview.install_url ?? "", "_blank", "noopener")}
          >
            App のインストール先を変更
          </Button>
        )}
      </div>

      <Table columns={COLUMNS} minWidth={880}>
        {renderRows()}
      </Table>

      {(isCreating || editTarget) && (
        <GitHubRepositorySettingsModal
          repository={editTarget}
          onClose={() => {
            setIsCreating(false);
            setEditTarget(null);
          }}
        />
      )}
      {logsTarget && (
        <GitHubWriteLogsModal repository={logsTarget} onClose={() => setLogsTarget(null)} />
      )}
    </PageWrapper>
  );
};

export default AdminGitHubRepositories;
